// Deep-space-theme hero cards for status / bug / feature events. Mirrors SpookyEventCard: a fixed
// 660px card zoomed-to-fit as one unit, paused off-screen. The night sky is a real <SpaceSky>
// component; the foreground "actor" artwork is injected from DeepSpaceScenes. "deep" theme only
// (gated in MessageList).
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import SpaceSky from "./SpaceSky.jsx";
import { DS_FEATURE, DS_BUG, DS_STATUS } from "./DeepSpaceScenes.js";
import "../deep-space-cards.css";
import "../deep-space-bug.css";
import "../deep-space-status.css";

const norm = (s) => (s || "").replace(/\s+/g, "").toLowerCase();

// Colour roles → { accent, tint-bg, tint-border }.
const C = {
  teal:   { a: "#35e0d0", bg: "rgba(53,224,208,.14)",  bd: "rgba(53,224,208,.4)" },
  amber:  { a: "#ffb454", bg: "rgba(255,180,84,.14)",  bd: "rgba(255,180,84,.4)" },
  green:  { a: "#4fd18a", bg: "rgba(79,209,138,.14)",  bd: "rgba(79,209,138,.4)" },
  danger: { a: "#ff6a7a", bg: "rgba(255,106,122,.14)", bd: "rgba(255,106,122,.4)" },
  blue:   { a: "#5fa8ff", bg: "rgba(95,168,255,.14)",  bd: "rgba(95,168,255,.4)" },
  violet: { a: "#c08bff", bg: "rgba(192,139,255,.14)", bd: "rgba(192,139,255,.4)" },
};

// status (normalised) → title + colour group. Scene visuals come from DS_STATUS[key]. Byline uses
// REAL event data (assignee else reporter), matching the spooky card (no placeholder names).
const STATUS = {
  pending:               { title: "Ready on the pad",       color: "teal" },
  inprogress:            { title: "Climbing to orbit",       color: "teal" },
  devtesting:            { title: "Flight test",             color: "teal" },
  qa:                    { title: "Running diagnostics",     color: "amber" },
  clienttesting:         { title: "Under observation",       color: "amber" },
  waitingbrief:          { title: "Scanning for signal",     color: "amber" },
  awaitingapproval:      { title: "Awaiting go / no-go",     color: "amber" },
  awaitingclient:        { title: "Transmitting…",           color: "amber" },
  onhold:                { title: "Holding position",        color: "amber" },
  releasedocsinprogress: { title: "Logging the flight",      color: "teal" },
  released:              { title: "Touchdown — released!",    color: "green" },
  completed:             { title: "Mission complete",         color: "green" },
};

export default function DeepSpaceEventCard({ kind, m, meta, onCardOpen }) {
  let scene, badge, title, chip, accent, chipColor, onOpen;
  const people = [];
  const add = (label, name) => { if (name) people.push([label, name]); };

  if (kind === "status") {
    const key = norm(meta.status);
    const info = STATUS[key] || STATUS.inprogress;
    scene = DS_STATUS[key] || DS_STATUS.inprogress;
    badge = "▲ Status update"; title = info.title; chip = meta.status;
    accent = info.color; chipColor = info.color;
    onOpen = () => onCardOpen?.({ ...meta, at: m.createdAt });
    if (meta.assignee) add("assigned to", meta.assignee);
    else add("reported by", meta.reporter);
  } else if (kind === "bug") {
    scene = DS_BUG; badge = "🛰 Anomaly detected"; title = "Impact on the hull…";
    chip = "Open"; accent = "danger"; chipColor = "danger";
    onOpen = () => onCardOpen?.(meta);
    add("reported by", meta.by || meta.reporter);
  } else if (kind === "story") {
    // No bespoke story scene — borrow the feature scene's art, but say what it actually is.
    scene = DS_FEATURE; badge = "✦ Story created"; title = "A new signal is charted…";
    chip = "New"; accent = "teal"; chipColor = "amber";
    onOpen = () => onCardOpen?.(meta);
    add("created by", meta.by || meta.reporter);
  } else {
    scene = DS_FEATURE; badge = "✦ Feature requested"; title = "A new signal is inbound…";
    chip = "New"; accent = "teal"; chipColor = "amber";
    onOpen = () => onCardOpen?.(meta);
    add("requested by", meta.by || meta.reporter);
  }

  const isDrift = scene === DS_STATUS.onhold; // On Hold: wrap the "Holding position" title to two lines
  const isDevTest = scene === DS_STATUS.devtesting; // Dev Testing garage → cycle the technician's quip
  const isLanding = scene === DS_STATUS.released;   // Released/Completed: narrow the sky to match the foreground (left:44%)
  const isRelease = scene === DS_STATUS.releasedocsinprogress; // mission-control room → cycle C-3PO's line
  const isCabin = scene === DS_STATUS.waitingbrief; // crew cabin → cycle the boss's line

  const A = C[accent] || C.teal;
  const CH = C[chipColor] || C.amber;
  const vars = {
    "--ds-accent": A.a, "--ds-tintbg": A.bg, "--ds-tintbd": A.bd,
    "--ds-chip": CH.a, "--ds-chipbg": CH.bg, "--ds-chipbd": CH.bd,
  };

  const fitRef = useRef(null);
  const cardRef = useRef(null);
  const actorRef = useRef(null);
  const [onScreen, setOnScreen] = useState(true);

  // Fit the fixed 660px card to its container with transform:scale (the design's approach), and set
  // the wrapper's height to the SCALED card height — a transformed element contributes no layout
  // height, so without this the row collapses and the card vanishes when the window is resized.
  useLayoutEffect(() => {
    const fit = () => {
      const f = fitRef.current, c = cardRef.current;
      if (!f || !c) return;
      const w = f.clientWidth || 660;
      const sc = Math.min(1, w / 660);
      c.style.transform = `scale(${sc})`;
      c.style.marginLeft = `${Math.max(0, (w - 660 * sc) / 2)}px`;
      f.style.height = `${c.offsetHeight * sc}px`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (fitRef.current) ro.observe(fitRef.current);
    window.addEventListener("resize", fit);
    const t1 = setTimeout(fit, 80), t2 = setTimeout(fit, 450); // catch late fonts/images
    return () => { ro.disconnect(); window.removeEventListener("resize", fit); clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    const el = fitRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { rootMargin: "150px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // On Hold scene only: recompute the astronaut↔rocket tether path each frame so both ends stay locked
  // to their independently-floating objects. Scoped to THIS card's actor (ids repeat across cards).
  // Paused while off-screen. Other scenes have no #dss-wire-svg, so the effect no-ops.
  useEffect(() => {
    const root = actorRef.current;
    const svg = root?.querySelector("#dss-wire-svg");
    if (!svg || !onScreen) return undefined;
    const p1 = svg.querySelector("#dss-wire-1");
    const p2 = svg.querySelector("#dss-wire-2");
    const astro = root.querySelector("#dss-astro");
    const rocket = root.querySelector("#dss-rocket");
    if (!p1 || !p2 || !astro || !rocket) return undefined;
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = svg.getBoundingClientRect();
      // The card is transform:scale'd to fit; getBoundingClientRect is post-scale while path coords are
      // in the svg's unscaled px space. clientWidth stays unscaled under transform, so this ratio is the
      // scale factor — divide client deltas by it to convert back to path space.
      const sc = (svg.clientWidth && s.width) ? s.width / svg.clientWidth : 1;
      const a = astro.getBoundingClientRect();
      const r = rocket.getBoundingClientRect();
      const ax = (a.left - s.left + a.width * 0.50) / sc; // astronaut waist (front)
      const ay = (a.top - s.top + a.height * 0.56) / sc;
      const rx = (r.left - s.left + r.width * 0.50) / sc; // rocket centre
      const ry = (r.top - s.top + r.height * 0.42) / sc;
      const px = ax - 92, py = ay - 38;
      const d =
        `M ${rx} ${ry} ` +
        `C ${rx + 34} ${ry - 40}, ${rx + 78} ${ry - 42}, ${px} ${py} ` +
        `C ${ax - 70} ${ay - 4}, ${ax - 40} ${ay + 30}, ${ax + 2} ${ay + 28} ` +
        `C ${ax + 30} ${ay + 26}, ${ax + 34} ${ay - 2}, ${ax + 12} ${ay - 12} ` +
        `C ${ax - 2} ${ay - 20}, ${ax - 20} ${ay - 14}, ${ax - 14} ${ay + 2}`;
      p1.setAttribute("d", d); p2.setAttribute("d", d);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [onScreen, scene]);

  // Dev Testing: the returning technician's quip. Seed a random line on mount, then advance every 20s
  // (one animation loop) so each pass shows a different one — same idea as the QA diagnostic text.
  useEffect(() => {
    if (!isDevTest) return undefined;
    const el = actorRef.current?.querySelector("#dss-dt-quip");
    if (!el) return undefined;
    const JOKES = ["Works on my machine!", "Ship it — probably fine.", "No bugs, just features.", "It passed… don't touch it.", "Flies great. Don't ask how."];
    let i = Math.floor(Math.random() * JOKES.length);
    el.textContent = JOKES[i];
    if (!onScreen) return undefined; // don't tick while paused off-screen
    const iv = setInterval(() => { i = (i + 1) % JOKES.length; el.textContent = JOKES[i]; }, 20000);
    return () => clearInterval(iv);
  }, [isDevTest, onScreen, scene]);

  // Release Docs: C-3PO's line cycles once per walk-on loop (22s, matching dss-botwalk/botsay).
  useEffect(() => {
    if (!isRelease) return undefined;
    const el = actorRef.current?.querySelector("#dss-c3po-line");
    if (!el) return undefined;
    const LINES = ["Did you hear that? They shut down the main reactor. We'll be destroyed for sure. This is madness.", "We are doomed.", "My parts are showing? Oh, my goodness, oh!", "As a matter of fact, I'm not even sure which planet I'm on."];
    let i = Math.floor(Math.random() * LINES.length);
    el.textContent = LINES[i];
    if (!onScreen) return undefined;
    const iv = setInterval(() => { i = (i + 1) % LINES.length; el.textContent = LINES[i]; }, 22000);
    return () => clearInterval(iv);
  }, [isRelease, onScreen, scene]);

  // Waiting Brief: the boss delivers one line per teleport-in; cycle it on the 40s loop boundary.
  useEffect(() => {
    if (!isCabin) return undefined;
    const el = actorRef.current?.querySelector("#dss-vader-line");
    if (!el) return undefined;
    const LINES = ["You misspelled 'deadline'. It was yesterday.", "I find your lack of progress… disturbing.", "The brief is ready. So is your performance review."];
    let i = Math.floor(Math.random() * LINES.length);
    el.textContent = LINES[i];
    if (!onScreen) return undefined;
    const iv = setInterval(() => { i = (i + 1) % LINES.length; el.textContent = LINES[i]; }, 40000);
    return () => clearInterval(iv);
  }, [isCabin, onScreen, scene]);

  return (
    <div ref={fitRef} className="ds-fit">
      <div ref={cardRef} className={`ds-card${kind === "bug" ? " ds-card--bug" : ""}${isDrift ? " ds-card--drift" : ""}${onScreen ? "" : " ds-card--paused"}`}
           style={vars}
           role="button" tabIndex={0} onClick={onOpen}
           onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}>
        <div className="ds-card__inner">
          <div className="ds-card__scene" aria-hidden>
            {scene.sky && <div className={`ds-card__sky${isLanding ? " ds-card__sky--44" : ""}`}><SpaceSky {...scene.sky} /></div>}
            <div className="ds-card__actor" ref={actorRef} dangerouslySetInnerHTML={{ __html: scene.html }} />
          </div>
          <div className="ds-card__scrim" aria-hidden />
          <div className="ds-card__text">
            <div className="ds-card__badgerow">
              <span className="ds-card__badge">{badge}</span>
              {meta.id ? <span className="ds-card__id">#{meta.id}</span> : null}
            </div>
            <h3 className="ds-card__title">{title}</h3>
            {meta.title ? <div className="ds-card__subject">{meta.title}</div> : null}
            {people.length > 0 && (
              <div className="ds-card__bylines">
                {people.map(([label, name], i) => (
                  <div key={i} className="ds-card__byline">{label} <b>{name}</b></div>
                ))}
              </div>
            )}
            <div className="ds-card__foot"><span className="ds-card__chip">{chip}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
